import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  addDays,
  berlinDate,
  diffDays,
  INQUIRY_STATUS_LABELS,
  ORDER_STATUS_LABELS,
  type Appointment,
  type FinanceDashboard,
  type Inquiry,
  type OfferSummary,
  type OrderSummary,
  type Paginated,
  type Task,
  type TaskSummary,
} from '@kontor/shared';
import { api, queryString } from '../api';
import { useSession } from '../session';
import { useNotices, useWidget } from '../notices';
import './dashboard.css';

const eur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const dayFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Berlin' });

function greeting(): string {
  // Die Stunde als Zahl lesen: Das deutsche Format der Stunde allein lautet „07 Uhr“ und wäre keine Zahl, dann stünde immer „Guten Abend“ da
  const part = new Intl.DateTimeFormat('de-DE', { hour: 'numeric', hourCycle: 'h23', timeZone: 'Europe/Berlin' }).formatToParts(new Date()).find((p) => p.type === 'hour');
  const hour = Number(part?.value ?? 12);
  return hour < 11 ? 'Guten Morgen' : hour < 18 ? 'Guten Tag' : 'Guten Abend';
}

/** „07.10.“ aus JJJJ-MM-TT */
const shortDate = (iso: string) => dayFormat.format(new Date(`${iso}T12:00:00Z`));
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function DashboardPage() {
  const { session, can, hasModule } = useSession();
  const today = berlinDate();
  const me = session?.user.id;

  const useInquiries = hasModule('inquiries') && can('inquiries.read');
  const useOffers = hasModule('offers') && can('offers.read');
  const useOrders = hasModule('orders') && can('orders.read');
  const useCalendar = hasModule('calendar') && can('calendar.read');
  const useFinance = hasModule('invoices_out') && can('invoices_out.read') && can('invoices_out.read_amounts');

  const inquiries = useWidget<Paginated<Inquiry>>(['inquiries'], `/inquiries${queryString({ status: 'new', perPage: 5 })}`, useInquiries);
  const offers = useWidget<Paginated<OfferSummary>>(['offers'], `/offers${queryString({ status: 'sent', perPage: 50 })}`, useOffers);
  const orders = useWidget<Paginated<OrderSummary>>(['orders'], `/orders${queryString({ status: 'in_progress', perPage: 5 })}`, useOrders);
  const summary = useWidget<{ data: TaskSummary }>(['task-summary', me], `/tasks/summary${queryString({ involvedUserId: me })}`, useCalendar && !!me);
  const tasks = useWidget<Paginated<Task>>(['tasks', me], `/tasks${queryString({ group: 'open', involvedUserId: me, perPage: 6 })}`, useCalendar && !!me);
  // „Heute“ zeigt nur die eigenen Termine: die, an denen man teilnimmt
  const appointments = useWidget<{ data: Appointment[] }>(['appointments', today, me], `/appointments${queryString({ from: today, to: today, involvedUserId: me })}`, useCalendar && !!me);
  const finance = useWidget<{ data: FinanceDashboard }>(['finance'], '/invoices-out/dashboard', useFinance);

  if (!session) return null;

  const todays = (appointments.data?.data ?? []).filter((a) => !a.masked && (me ? a.participantUserIds.includes(me) || a.createdByUserId === me : true)).sort((a, b) => a.start.localeCompare(b.start));
  const overdue = summary.data?.data.overdue ?? 0;
  const dueToday = summary.data?.data.today ?? 0;
  const fin = finance.data?.data;

  // Hinweise: nur, was jetzt Aufmerksamkeit braucht (gleiche Liste wie die Glocke in der Kopfleiste)
  const { notices } = useNotices();

  const dayParts: string[] = [];
  if (useCalendar && appointments.data) dayParts.push(plural(todays.length, 'Termin', 'Termine'));
  if (useCalendar && summary.data) dayParts.push(plural(dueToday + overdue, 'fällige Aufgabe', 'fällige Aufgaben'));
  const dayLine = dayParts.length === 0 ? '' : dayParts.every((p) => p.startsWith('0 ')) ? 'Für heute ist nichts eingetragen.' : `Heute stehen ${dayParts.join(' und ')} an.`;

  const nothingVisible = !useInquiries && !useOffers && !useOrders && !useCalendar && !useFinance;
  const todayLabel = new Intl.DateTimeFormat('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Berlin' }).format(new Date());

  const actions = [
    { show: hasModule('inquiries') && can('inquiries.write'), to: '/anfragen/neu', label: 'Neue Anfrage' },
    { show: hasModule('offers') && can('offers.write'), to: '/angebote/neu', label: 'Neues Angebot' },
    { show: hasModule('orders') && can('orders.write'), to: '/auftraege/neu', label: 'Neuer Auftrag' },
    { show: hasModule('calendar') && can('calendar.write'), to: '/kalender/aufgaben/neu', label: 'Neue Aufgabe' },
    { show: hasModule('calendar') && can('calendar.read'), to: '/kalender/termine/neu', label: 'Neuer Termin' },
  ].filter((a) => a.show);

  return (
    <div className="dash">
      <header className="dash__hero">
        <div>
          <p className="dash__date">{todayLabel}</p>
          <h1>{greeting()}, {session.user.name.split(' ')[0]}</h1>
          {dayLine && <p className="dash__line">{dayLine}</p>}
        </div>
        {actions.length > 0 && (
          <div className="dash__actions" role="group" aria-label="Schnellaktionen">
            {actions.map((a, i) => (
              <Link key={a.to} to={a.to} className={`btn btn--sm ${i === 0 ? 'btn--primary' : 'btn--secondary'}`}>+ {a.label}</Link>
            ))}
          </div>
        )}
      </header>

      {nothingVisible && (
        <p className="dash__empty card">Für Ihre Rolle gibt es hier noch nichts anzuzeigen. Die Bereiche finden Sie im Menü.</p>
      )}

      {(useInquiries || useOffers || useOrders || useCalendar || useFinance) && (
        <Section title="Auf einen Blick">
          <div className="cues">
            {useInquiries && <Cue to="/anfragen?status=new" value={inquiries.data?.meta.total} label="neue Anfragen" query={inquiries} />}
            {useOffers && <Cue to="/angebote?status=sent" value={offers.data?.meta.total} label="Angebote ohne Antwort" query={offers} />}
            {useOrders && <Cue to="/auftraege?status=in_progress" value={orders.data?.meta.total} label="Aufträge in Arbeit" query={orders} />}
            {useCalendar && <Cue to="/kalender/aufgaben?mine=1" value={summary.data ? summary.data.data.open : undefined} label="meine offenen Aufgaben" query={summary} />}
            {useCalendar && <Cue to="/kalender/aufgaben?group=overdue&mine=1" value={summary.data ? overdue : undefined} label="überfällig" query={summary} alert={overdue > 0} />}
            {useFinance && <Cue to="/rechnungen?payment=open" value={fin ? eur.format(fin.openCents / 100) : undefined} label="offene Posten" query={finance} />}
            {useFinance && <Cue to="/rechnungen?payment=overdue" value={fin ? eur.format(fin.overdueCents / 100) : undefined} label="Rechnungen überfällig" query={finance} alert={(fin?.overdueCount ?? 0) > 0} />}
          </div>
        </Section>
      )}

      {(useInquiries || useOffers || useCalendar || useFinance) && (
        <Section title="Hinweise">
          <div className="dash-grid">
            <Card title="Was Ihre Aufmerksamkeit braucht" wide status={notices.length === 0 ? (isLoading([inquiries, offers, summary, finance]) ? 'loading' : 'empty') : 'ready'} emptyText="Im Moment gibt es nichts, das Ihre Aufmerksamkeit braucht.">
              <ul className="notices">
                {notices.map((n) => (
                  <li key={n.key}>
                    <Link to={n.to} className={`notice notice--${n.tone}`}>{n.text}</Link>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </Section>
      )}

      {useCalendar && (
        <Section title="Mein Tag">
          <div className="dash-grid">
            <Card title="Heute" to="/kalender/termine" linkLabel="Kalender" query={appointments} status={status(appointments, todays.length)} emptyText="Heute sind keine Termine für Sie eingetragen.">
              <ul className="rows">
                {todays.map((a) => (
                  <li key={a.id}>
                    <Link to={`/kalender/termine/${a.id}`} className="row">
                      <span className="row__time">{a.allDay ? 'ganztägig' : a.start.slice(11, 16)}</span>
                      <span className="row__main">{a.title}{a.location && <small>{a.location}</small>}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>

            <Card title="Meine Aufgaben" to="/kalender/aufgaben?mine=1" linkLabel="Alle Aufgaben" query={tasks} status={status(tasks, tasks.data?.data.length ?? 0)} emptyText="Keine offenen Aufgaben. Das ist ein guter Stand.">
              <ul className="rows">
                {tasks.data?.data.map((t) => {
                  const late = !!t.dueDate && t.dueDate < today;
                  return (
                    <li key={t.id}>
                      <Link to={`/kalender/aufgaben/${t.id}`} className="row">
                        <span className={`row__time${late ? ' row__time--alert' : ''}`}>{t.dueDate ? (t.dueDate === today ? 'heute' : shortDate(t.dueDate)) : 'ohne Datum'}</span>
                        <span className="row__main">{t.title}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </Card>
          </div>
        </Section>
      )}

      {(useInquiries || useOffers || useOrders) && (
        <Section title="Vertrieb">
          <div className="dash-grid">
            {useInquiries && (
              <Card title="Neue Anfragen" to="/anfragen?status=new" linkLabel="Alle Anfragen" query={inquiries} status={status(inquiries, inquiries.data?.data.length ?? 0)} emptyText={`Keine Anfragen mit dem Stand „${INQUIRY_STATUS_LABELS.new}“.`}>
                <ul className="rows">
                  {inquiries.data?.data.map((i) => (
                    <li key={i.id}>
                      <Link to={`/anfragen/${i.id}`} className="row">
                        <span className="row__time">{shortDate(i.createdAt.slice(0, 10))}</span>
                        <span className="row__main">{i.contact.name}<small>{i.contact.company ?? i.type}</small></span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {useOffers && (
              <Card title="Angebote ohne Antwort" to="/angebote?status=sent" linkLabel="Alle Angebote" query={offers} status={status(offers, offers.data?.data.length ?? 0)} emptyText="Alle versendeten Angebote sind beantwortet.">
                <ul className="rows">
                  {[...(offers.data?.data ?? [])].sort((a, b) => (a.validUntil ?? '9999').localeCompare(b.validUntil ?? '9999')).slice(0, 5).map((o) => (
                    <li key={o.id}>
                      <Link to={`/angebote/${o.id}`} className="row">
                        <span className={`row__time${o.validUntil && o.validUntil < today ? ' row__time--alert' : ''}`}>{o.validUntil ? `bis ${shortDate(o.validUntil)}` : 'offen'}</span>
                        <span className="row__main">{o.contactName}<small>{o.number ?? 'Entwurf'} · {eur.format(o.grossCents / 100)}</small></span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {useOrders && (
              <Card title="Aufträge in Arbeit" to="/auftraege?status=in_progress" linkLabel="Alle Aufträge" query={orders} status={status(orders, orders.data?.data.length ?? 0)} emptyText={`Kein Auftrag hat den Stand „${ORDER_STATUS_LABELS.in_progress}“.`}>
                <ul className="rows">
                  {orders.data?.data.map((o) => (
                    <li key={o.id}>
                      <Link to={`/auftraege/${o.id}`} className="row">
                        <span className="row__time">{o.serviceEnd ? `bis ${shortDate(o.serviceEnd)}` : o.number}</span>
                        <span className="row__main">{o.contactName}<small>{o.subject}</small></span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>
        </Section>
      )}

      {useFinance && (
        <Section title="Finanzen" to="/rechnungen" linkLabel="Ausgangsrechnungen">
          <div className="dash-grid">
            <Card title="Umsatz (netto)" status={status(finance, fin ? 1 : 0)} query={finance} emptyText="Noch keine Rechnungen.">
              {fin && (
                <>
                  <p className="stat">{eur.format(fin.revenueMonthCents / 100)}</p>
                  <p className="stat__sub">diesen Monat{trend(fin.revenueMonthCents, fin.revenueLastMonthCents)}</p>
                  <dl className="statlist">
                    <dt>Vormonat</dt>
                    <dd>{eur.format(fin.revenueLastMonthCents / 100)}</dd>
                    <dt>Dieses Jahr</dt>
                    <dd>{eur.format(fin.revenueYearCents / 100)}</dd>
                  </dl>
                </>
              )}
            </Card>

            <Card title="Offene Posten" to="/rechnungen?payment=open" linkLabel="Ansehen" query={finance} status={status(finance, fin ? 1 : 0)} emptyText="Noch keine Rechnungen.">
              {fin && (
                <>
                  <p className="stat">{eur.format(fin.openCents / 100)}</p>
                  <p className="stat__sub">{fin.openCount === 0 ? 'keine offene Rechnung' : plural(fin.openCount, 'offene Rechnung', 'offene Rechnungen')}</p>
                  <dl className="statlist">
                    <dt>Davon überfällig</dt>
                    <dd className={fin.overdueCount > 0 ? 'statlist__alert' : undefined}>{fin.overdueCount > 0 ? `${eur.format(fin.overdueCents / 100)} (${fin.overdueCount})` : 'keine'}</dd>
                    <dt>Geldeingang diesen Monat</dt>
                    <dd>{eur.format(fin.receivedMonthCents / 100)}</dd>
                    <dt>Rechnungsentwürfe</dt>
                    <dd>{fin.draftCount === 0 ? 'keine' : <Link to="/rechnungen?status=draft">{fin.draftCount}</Link>}</dd>
                  </dl>
                </>
              )}
            </Card>

            <Card title="Stärkste Kunden dieses Jahr" query={finance} status={status(finance, fin?.topCustomers.length ?? 0)} emptyText="Noch kein Umsatz in diesem Jahr.">
              <ul className="rows">
                {fin?.topCustomers.map((c) => (
                  <li key={c.contactId}>
                    <Link to={`/kontakte/${c.contactId}`} className="row row--money">
                      <span className="row__main">{c.name}</span>
                      <span className="row__amount">{eur.format(c.revenueCents / 100)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>

            <Card title="Umsatz der letzten sechs Monate" wide query={finance} status={status(finance, fin ? 1 : 0)} emptyText="Noch keine Rechnungen.">
              {fin && <Bars months={fin.months} />}
            </Card>
          </div>
        </Section>
      )}
    </div>
  );
}

/** Veränderung zum Vormonat als Text, leer wenn es keinen Vergleich gibt */
function trend(now: number, before: number): string {
  if (before <= 0) return '';
  const pct = Math.round(((now - before) / before) * 100);
  return ` · ${pct >= 0 ? '+' : ''}${pct} % zum Vormonat`;
}

const monthName = new Intl.DateTimeFormat('de-DE', { month: 'short', timeZone: 'UTC' });

/** Säulen je Monat, die Höhe folgt dem größten Wert. Der aktuelle Monat ist hervorgehoben. */
function Bars({ months }: { months: { month: string; revenueCents: number }[] }) {
  const max = Math.max(1, ...months.map((m) => m.revenueCents));
  return (
    <ul className="bars" aria-label="Umsatz je Monat">
      {months.map((m, i) => (
        <li key={m.month} className={`bars__col${i === months.length - 1 ? ' is-current' : ''}`}>
          <span className="bars__value">{eur.format(m.revenueCents / 100)}</span>
          <span className="bars__track">
            <span className="bars__bar" style={{ height: `${Math.max(2, Math.round((Math.max(0, m.revenueCents) / max) * 100))}%` }} />
          </span>
          <span className="bars__label">{monthName.format(new Date(`${m.month}-01T12:00:00Z`))}</span>
        </li>
      ))}
    </ul>
  );
}

/** Bereich der Übersicht mit Überschrift und eigener Fläche, damit die Gruppen auf einen Blick getrennt sind */
function Section({ title, to, linkLabel, children }: { title: string; to?: string; linkLabel?: string; children: ReactNode }) {
  const id = `dash-${title.toLowerCase().replace(/[^a-zäöüß]+/g, '-')}`;
  return (
    <section className="dash-section" aria-labelledby={id}>
      <header className="dash-section__head">
        <h2 id={id}>{title}</h2>
        {to && <Link to={to} className="widget__more">{linkLabel ?? 'Alle'} <span aria-hidden="true">›</span></Link>}
      </header>
      {children}
    </section>
  );
}

const isLoading = (queries: UseQueryResult[]) => queries.some((q) => q.fetchStatus !== 'idle' && q.isLoading);

function status(q: UseQueryResult, count: number): 'loading' | 'error' | 'empty' | 'ready' {
  if (q.isError) return 'error';
  if (q.isLoading) return 'loading';
  return count === 0 ? 'empty' : 'ready';
}

/** Kachel mit einer Kennzahl, ein Klick führt zur passenden gefilterten Liste */
function Cue({ to, value, label, query, alert }: { to: string; value: number | string | undefined; label: string; query: UseQueryResult; alert?: boolean }) {
  return (
    <Link to={to} className={`cue${alert ? ' cue--alert' : ''}`}>
      <span className="cue__value">{query.isError ? '!' : value ?? '–'}</span>
      <span className="cue__label">{query.isError ? 'nicht ladbar' : label}</span>
    </Link>
  );
}

/** Rahmen eines Widgets mit Titel, Link zur Liste und den Zuständen Laden, Fehler und leer */
function Card({ title, to, linkLabel, query, status: state, emptyText, wide, children }: { title: string; to?: string; linkLabel?: string; query?: UseQueryResult; status: 'loading' | 'error' | 'empty' | 'ready'; emptyText: string; wide?: boolean; children: ReactNode }) {
  return (
    <section className={`widget${wide ? ' widget--wide' : ''}`} aria-busy={state === 'loading'}>
      <header className="widget__head">
        <h2>{title}</h2>
        {to && <Link to={to} className="widget__more">{linkLabel ?? 'Alle'} <span aria-hidden="true">›</span></Link>}
      </header>
      {state === 'loading' && <div className="widget__skeleton" aria-label="Wird geladen"><span /><span /><span /></div>}
      {state === 'error' && (
        <p className="widget__state">
          Die Daten konnten nicht geladen werden.{' '}
          {query && <button type="button" className="linkbutton" onClick={() => void query.refetch()}>Erneut versuchen</button>}
        </p>
      )}
      {state === 'empty' && <p className="widget__state widget__state--empty">{emptyText}</p>}
      {state === 'ready' && children}
    </section>
  );
}
